import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Stack, Typography, Button, Box, IconButton, LinearProgress, Alert, Tooltip, Skeleton, alpha,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { SalonMember, TaskAttachment, taskApi, taskErrorMessage } from '../../services/taskService';
import { formatDateTime, formatFileSize, userLabel } from './taskShared';
import { withMemberName } from './TaskHistory';
import { SectionCard, EmptyState, useConfirm } from '../../components/ui';
import { tokens } from '../../theme';

interface Props {
  taskId: string;
  currentUserId: string;
  canUpload: boolean;
  canManage: boolean;
  onChanged: () => void;
  onNotify: (msg: string, severity: 'success' | 'error' | 'info') => void;
  onOpenAttachment: (attachmentId: string, isImage: boolean, name: string) => void;
  /** Lista osob salonu — do podpisu, kto dodal plik. */
  members?: SalonMember[];
}

const ACCEPTED = [
  '.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.txt', '.csv',
].join(',');

const MAX_MB = 10;

/** Ikona i krotka nazwa typu pliku na podstawie rozszerzenia. */
const fileKind = (a: TaskAttachment): { icon: React.ReactNode; label: string } => {
  const ext = (a.originalName.includes('.') ? a.originalName.split('.').pop() || '' : '').toLowerCase();
  const label = ext ? ext.toUpperCase() : 'Plik';
  if (a.isImage) return { icon: <ImageOutlinedIcon />, label };
  if (ext === 'pdf' || a.mimeType === 'application/pdf') return { icon: <PictureAsPdfOutlinedIcon />, label: 'PDF' };
  if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return { icon: <TableChartOutlinedIcon />, label };
  if (['ppt', 'pptx'].includes(ext)) return { icon: <SlideshowOutlinedIcon />, label };
  if (['doc', 'docx', 'odt', 'txt'].includes(ext)) return { icon: <DescriptionOutlinedIcon />, label };
  return { icon: <InsertDriveFileOutlinedIcon />, label };
};

export const TaskAttachments: React.FC<Props> = ({
  taskId, currentUserId, canUpload, canManage, onChanged, onNotify, onOpenAttachment, members,
}) => {
  const confirm = useConfirm();
  const [items, setItems] = useState<TaskAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  /** Blob URLs of image previews, keyed by attachment id. */
  const thumbUrls = useRef<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await taskApi.listAttachments(taskId));
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się wczytać załączników.'));
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  // Images live behind an authenticated endpoint, so previews are fetched as
  // blobs. The URLs are tracked in a ref (not in state) so fetching a preview
  // cannot re-trigger this effect, and every URL is released on unmount.
  useEffect(() => {
    let active = true;
    const missing = items.filter((i) => i.isImage && !thumbUrls.current[i.id]);
    if (missing.length === 0) return undefined;

    Promise.all(
      missing.map(async (img) => {
        try {
          const blob = await taskApi.fetchAttachmentBlob(img.id, true);
          return [img.id, URL.createObjectURL(blob)] as const;
        } catch {
          return null;
        }
      }),
    ).then((pairs) => {
      const next: Record<string, string> = {};
      pairs.forEach((pair) => {
        if (!pair) return;
        if (!active) {
          URL.revokeObjectURL(pair[1]);
          return;
        }
        thumbUrls.current[pair[0]] = pair[1];
        next[pair[0]] = pair[1];
      });
      if (active && Object.keys(next).length) setThumbs((prev) => ({ ...prev, ...next }));
    });

    return () => { active = false; };
  }, [items]);

  useEffect(() => {
    const urls = thumbUrls.current;
    return () => { Object.values(urls).forEach((url) => URL.revokeObjectURL(url)); };
  }, []);

  const upload = async (files: File[]) => {
    if (files.length === 0) return;
    const tooBig = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) {
      setError(`Plik „${tooBig.name}” przekracza ${MAX_MB} MB. Zmniejsz go albo wybierz inny plik.`);
      return;
    }
    setUploading(true);
    setPercent(0);
    setError('');
    try {
      await taskApi.uploadAttachments(taskId, files.slice(0, 10), { onProgress: setPercent });
      await load();
      onChanged();
      onNotify('Załączniki zostały dodane.', 'success');
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się wysłać pliku.'));
    } finally {
      setUploading(false);
      setPercent(0);
    }
  };

  const remove = async (attachment: TaskAttachment) => {
    const ok = await confirm({
      title: 'Usunąć załącznik?',
      details: (
        <>
          <Box component="span" sx={{ display: 'block', fontWeight: 700, wordBreak: 'break-word' }}>{attachment.originalName}</Box>
          <Box component="span" sx={{ display: 'block', color: tokens.muted, fontSize: '0.8125rem' }}>{formatFileSize(attachment.sizeBytes)}</Box>
        </>
      ),
      description: 'Plik zniknie z zadania dla wszystkich osób. Tej operacji nie można cofnąć.',
      confirmLabel: 'Usuń załącznik',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await taskApi.deleteAttachment(attachment.id);
      const url = thumbUrls.current[attachment.id];
      if (url) {
        URL.revokeObjectURL(url);
        delete thumbUrls.current[attachment.id];
      }
      setThumbs((prev) => {
        const next = { ...prev };
        delete next[attachment.id];
        return next;
      });
      await load();
      onChanged();
      onNotify('Załącznik usunięty.', 'success');
    } catch (err) {
      onNotify(taskErrorMessage(err, 'Nie udało się usunąć załącznika.'), 'error');
    }
  };

  const images = items.filter((i) => i.isImage);
  const documents = items.filter((i) => !i.isImage);
  const canDelete = (a: TaskAttachment) => canManage || a.uploaderId === currentUserId;
  const openPicker = () => { if (!uploading) inputRef.current?.click(); };

  return (
    <SectionCard
      title={(
        <>
          Załączniki
          {items.length > 0 && (
            <Box component="span" sx={{ ml: 0.75, color: tokens.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {items.length}
            </Box>
          )}
        </>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={ACCEPTED}
        onChange={(e) => {
          upload(Array.from(e.target.files || []));
          if (inputRef.current) inputRef.current.value = '';
        }}
      />

      {loading ? (
        <Stack gap={1.5} aria-busy="true" aria-label="Ładowanie załączników">
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" gap={1.5} alignItems="center">
              <Skeleton variant="rounded" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="45%" height={18} />
                <Skeleton width="30%" height={14} />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : items.length === 0 && !canUpload ? (
        <EmptyState compact icon={<AttachFileIcon />} title="Brak załączników" description="Pliki dodane do zadania lub komentarzy pojawią się tutaj." />
      ) : (
        <>
          {images.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))',
                gap: 1.5,
                mb: documents.length ? 2 : 0,
              }}
            >
              {images.map((img) => (
                <Box key={img.id} sx={{ minWidth: 0 }}>
                  <Box sx={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', bgcolor: tokens.surfaceStrong, aspectRatio: '4 / 3' }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onOpenAttachment(img.id, true, img.originalName)}
                      aria-label={`Otwórz ${img.originalName}`}
                      sx={{
                        display: 'grid', placeItems: 'center', width: '100%', height: '100%', p: 0, m: 0, border: 0,
                        bgcolor: 'transparent', cursor: 'pointer', color: tokens.subtle,
                        '&:hover img': { opacity: 0.9 },
                        '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 },
                      }}
                    >
                      {thumbs[img.id] ? (
                        <Box component="img" src={thumbs[img.id]} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity .15s ease' }} />
                      ) : (
                        <ImageOutlinedIcon />
                      )}
                    </Box>
                    {canDelete(img) && (
                      <Tooltip title="Usuń">
                        <IconButton
                          size="small"
                          aria-label={`Usuń ${img.originalName}`}
                          onClick={() => remove(img)}
                          sx={{
                            position: 'absolute', top: 6, right: 6,
                            bgcolor: alpha(tokens.paper, 0.92),
                            '&:hover': { bgcolor: tokens.paper, color: 'error.main' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography noWrap title={img.originalName} sx={{ mt: 0.75, fontSize: '0.8125rem', fontWeight: 600 }}>
                    {img.originalName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                    {formatFileSize(img.sizeBytes)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {documents.length > 0 && (
            <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
              {documents.map((doc) => {
                const kind = fileKind(doc);
                return (
                  <Stack key={doc.id} direction="row" alignItems="center" gap={1.5} sx={{ py: 1.25 }}>
                    <Box
                      sx={{
                        width: 40, height: 40, borderRadius: '10px', flexShrink: 0, display: 'grid', placeItems: 'center',
                        bgcolor: tokens.plumSoft, color: tokens.plum, '& .MuiSvgIcon-root': { fontSize: 22 },
                      }}
                    >
                      {kind.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography noWrap title={doc.originalName} sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                        {doc.originalName}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {kind.label} · {formatFileSize(doc.sizeBytes)}
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {' · '}{userLabel(withMemberName(doc.uploader, members))} · {formatDateTime(doc.createdAt)}
                        </Box>
                      </Typography>
                    </Box>
                    <Tooltip title="Pobierz">
                      <IconButton aria-label={`Pobierz ${doc.originalName}`} onClick={() => onOpenAttachment(doc.id, false, doc.originalName)}>
                        <DownloadOutlinedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    {canDelete(doc) && (
                      <Tooltip title="Usuń">
                        <IconButton
                          aria-label={`Usuń ${doc.originalName}`}
                          onClick={() => remove(doc)}
                          sx={{ ml: -0.5, '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}

          {canUpload && (
            <Box
              role="button"
              tabIndex={0}
              aria-label="Dodaj pliki do zadania"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                upload(Array.from(e.dataTransfer.files || []));
              }}
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPicker();
                }
              }}
              sx={{
                mt: items.length ? 2 : 0,
                border: `1px dashed ${dragOver ? tokens.plum : tokens.lineStrong}`,
                bgcolor: dragOver ? tokens.plumSofter : tokens.surface,
                borderRadius: '12px',
                px: 2,
                py: 1.75,
                cursor: uploading ? 'progress' : 'pointer',
                transition: 'border-color .15s ease, background-color .15s ease',
                '&:hover': { borderColor: tokens.plumLight, bgcolor: tokens.plumSofter },
                '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: tokens.paper, color: tokens.plum }}>
                  <CloudUploadOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                    <Box component="span" sx={{ color: tokens.plum }}>Wybierz pliki</Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> albo przeciągnij je tutaj</Box>
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>
                    Zdjęcia, PDF, dokumenty biurowe, TXT i CSV · do {MAX_MB} MB na plik
                  </Typography>
                </Box>
              </Stack>
              {uploading && (
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress variant="determinate" value={percent} />
                  <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                    Wysyłanie… {percent}%
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </SectionCard>
  );
};

export default TaskAttachments;
